import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { getApiBaseUrl } from "@/utils/helpers";

export interface Notification {
  _id: string;
  userId: number;
  type: "chat";
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  channelId: string;
  senderId: number;
  data?: Record<string, any>;
  redirectUrl?: string;
}

interface NotificationsResponse {
  notifications: Notification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  unreadCount: number;
}

interface UnreadCountResponse {
  count: number;
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Fetch notifications
  const fetchNotifications = useCallback(async (page = 1, limit = 20) => {
    try {
      setLoading(true);
      const response = await fetch(getApiBaseUrl(`notifications?page=${page}&limit=${limit}`), {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch notifications");
      }

      const data: NotificationsResponse = await response.json();
      setNotifications(data.notifications);
      setUnreadCount(data.unreadCount);
      setPagination(data.pagination);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch unread count only
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await fetch(getApiBaseUrl("notifications/unread-count"), {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch unread count");
      }

      const data: UnreadCountResponse = await response.json();
      setUnreadCount(data.count);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  }, []);

  // Mark specific notifications as read
  const markAsRead = useCallback(async (notificationIds: string[]) => {
    try {
      const response = await fetch(getApiBaseUrl("notifications/mark-read"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ notificationIds }),
      });

      if (!response.ok) {
        throw new Error("Failed to mark notifications as read");
      }

      // Update local state
      setNotifications((prev) =>
        prev.map((notification) =>
          notificationIds.includes(notification._id)
            ? { ...notification, isRead: true }
            : notification
        )
      );

      // Update unread count
      setUnreadCount((prev) => Math.max(0, prev - notificationIds.length));

      return true;
    } catch (error) {
      console.error("Error marking notifications as read:", error);
      toast.error("Failed to mark notifications as read");
      return false;
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await fetch(getApiBaseUrl("notifications/mark-all-read"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to mark all notifications as read");
      }

      // Update local state
      setNotifications((prev) => prev.map((notification) => ({ ...notification, isRead: true })));
      setUnreadCount(0);

      toast.success("All notifications marked as read");
      return true;
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      toast.error("Failed to mark all notifications as read");
      return false;
    }
  }, []);

  // Create a new notification (for testing or programmatic creation)
  const createNotification = useCallback(
    async (notificationData: {
      userId: number;
      type: Notification["type"];
      title: string;
      message: string;
      channelId?: string;
      senderId?: number;
      data?: Record<string, any>;
      redirectUrl?: string;
    }) => {
      try {
        const response = await fetch(getApiBaseUrl("notifications"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(notificationData),
        });

        if (!response.ok) {
          throw new Error("Failed to create notification");
        }

        const data = await response.json();

        // Refresh notifications if this is for the current user
        if (notificationData.userId === parseInt(localStorage.getItem("currentUserId") || "0")) {
          await fetchNotifications();
        }

        return data.notification;
      } catch (error) {
        console.error("Error creating notification:", error);
        toast.error("Failed to create notification");
        return null;
      }
    },
    [fetchNotifications]
  );

  // Load notifications on mount
  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, [fetchNotifications, fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    pagination,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    createNotification,
  };
};
